import * as Sentry from '@sentry/browser';
import { User } from '@sentry/browser';

const targets = [
  'http://dashboard.superdispatch.com',
  'https://carrier.superdispatch.com',
  'https://api.loadboard.superdispatch.com',
  'https://media.superdispatch.com',
];

const WARNING_MESSAGES = ['network', 'ws.zoominfo.com'];

function getSentryDsn() {
  switch (import.meta.env.VITE_APP_NAME) {
    case 'ctms':
      return 'https://773023b6b83842f3acbd679e422932ec@o181974.ingest.us.sentry.io/1297423';
    case 'ctmsw':
      return 'https://8f3a87092124a2a237f2a6e49ac0ccc2@o181974.ingest.us.sentry.io/4509438863605760';
    case 'slbd':
      return 'https://23f7053a350c8f5f329b826b7b8459df@o181974.ingest.us.sentry.io/4509438851284992';
    case 'slbm':
      return 'https://e7c46154a70b3cdfebbd6250eddb2b07@o181974.ingest.us.sentry.io/4509438860525568';
  }
}

export function init() {
  Sentry.init({
    dsn: getSentryDsn(),
    sendDefaultPii: true,
    release: import.meta.env.VITE_APP_RELEASE,
    environment: import.meta.env.VITE_APP_TARGET,
    integrations: [
      Sentry.extraErrorDataIntegration(),
      Sentry.featureFlagsIntegration(),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [400, 599],
        failedRequestTargets: targets,
      }),
    ],
    tracesSampleRate:
      import.meta.env.VITE_APP_TARGET === 'production' ? 1 : 0.1,
    tracePropagationTargets: targets,
    denyUrls: [/googletagmanager\.com\/gtm\.js/],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    ignoreErrors: [
      /feature named `[^`]*` was not found/,
      /Unauthorized/,
      /Failed to execute 'importScripts' on 'WorkerGlobalScope': The script at 'blob:.*' failed to load/,
      /TypeError: Cannot read properties of null \(reading 'removeLayer'\)/,
      /Failed to fetch/,
      /^Load failed$/,
      /this.errorCb is not a function. \(In 'this.errorCb(y)', 'this.errorCb' is null\)/,
      /null is not an object \(evaluating 'this._map.removeLayer'\)/,
      /undefined is not an object \(evaluating 'E._leaflet_pos'\)/,
      /Cannot read properties of undefined \(reading '_leaflet_pos'\)/,
      /Blocked 'style' from 'inline:'/,
      /ArrayBuffer at index \d+ is already detached/,
    ],
    beforeSend(event) {
      const hasUserScriptFrame = event.exception?.values?.some((value) =>
        value.stacktrace?.frames?.some((f) =>
          f.filename?.startsWith('user-script:'),
        ),
      );
      if (hasUserScriptFrame) {
        return null;
      }

      const hasThirdPartyTagFrame = event.exception?.values?.some((value) =>
        value.stacktrace?.frames?.some((f) =>
          f.filename?.includes('googletagmanager.com'),
        ),
      );
      if (hasThirdPartyTagFrame) {
        return null;
      }

      if (WARNING_MESSAGES.includes((event.message || '').toLowerCase())) {
        event.level = 'warning';
      }
      return event;
    },
  });
}

export function identifyUser(user: User) {
  Sentry.setUser(user);
}

export function cleanUser() {
  Sentry.setUser(null);
}

export function getLogger() {
  return Sentry.logger;
}

export function addContext(key: string, data: Record<string, unknown>) {
  Sentry.setExtra(key, data);
}
